<template>
  <b-card>
    <!-- form -->
    <validation-observer ref="resetForm" #default="{ invalid }">
      <b-form class="form-reset" ref="reset" @submit.prevent="changePassword">
        <b-row>
          <!-- old password -->
          <b-col md="6">
            <b-form-group :label="$t('message.Old_Password')" label-for="account-old-password">
              <validation-provider
                #default="{ errors }"
                :name="$t('message.Old_Password')"
                vid="passwordValueOld"
                rules="required"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="account-old-password"
                    v-model="passwordValueOld"
                    name="old-password"
                    :type="passwordFieldTypeOld"
                    :placeholder="$t('message.Old_Password')"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      :icon="passwordToggleIconOld"
                      class="cursor-pointer"
                      @click="togglePasswordOld"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!--/ old password -->
        </b-row>
        <b-row>
          <!-- password requirements -->
          <b-col cols="12">
            <b-form-group>
              <label class="mb-1">{{ $t('message.password_requirements_title') }}</label>
              <div class="password-requirements">
                <div
                  v-for="req in requirements"
                  :key="req.key"
                  class="requirement-item"
                  :class="req.met ? 'requirement-met' : 'requirement-unmet'"
                >
                  <feather-icon
                    :icon="req.met ? 'CheckIcon' : 'XIcon'"
                    size="16"
                    class="mr-50"
                  />
                  <span>{{ req.text }}</span>
                </div>
              </div>
            </b-form-group>
          </b-col>
          <!--/ password requirements -->

          <!-- new password -->
          <b-col md="6">
            <b-form-group label-for="account-new-password" :label="$t('message.New_Password')">
              <validation-provider
                #default="{ errors }"
                :name="$t('message.New_Password')"
                vid="newPasswordValue"
                rules="required"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="account-new-password"
                    v-model="newPasswordValue"
                    :type="passwordFieldTypeNew"
                    name="newPasswordValue"
                    :placeholder="$t('message.New_Password')"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      :icon="passwordToggleIconNew"
                      class="cursor-pointer"
                      @click="togglePasswordNew"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!--/ new password -->

          <!-- retype password -->
          <b-col md="6">
            <b-form-group
              label-for="account-retype-new-password"
              :label="$t('message.Retype_New_Password')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('message.Retype_New_Password')"
                vid="retypePassword"
                rules="required|confirmed:newPasswordValue"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="account-retype-new-password"
                    v-model="retypePassword"
                    :type="passwordFieldTypeRetype"
                    name="retypePassword"
                    :placeholder="$t('message.Retype_New_Password')"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      :icon="passwordToggleIconRetype"
                      class="cursor-pointer"
                      @click="togglePasswordRetype"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!--/ retype password -->

          <!-- buttons -->
          <b-col cols="12">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="success"
              class="mt-1 mr-1"
              type="submit"
              :disabled="invalid || !allRequirementsMet || isSubmitting"
            >
              <b-spinner v-if="isSubmitting" small class="mr-50" />
              <span>{{ isSubmitting ? $t('message.processing') : $t('message.save_changes') }}</span>
            </b-button>
          </b-col>
          <!--/ buttons -->
        </b-row>
      </b-form>
    </validation-observer>
  </b-card>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { BButton, BForm, BFormGroup, BFormInput, BRow, BCol, BCard, BInputGroup, BInputGroupAppend, BSpinner } from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import { required, confirmed, password } from "@validations";
import { passwordRequirements } from "@core/utils/validations/validators";
import i18n from '@/libs/i18n';
import { useLambda } from '@/views/habit/awsLambda'
import { useCognito } from '@/views/habit/awsCognito'
import useNotifications from '@/composables/useNotifications'
import { onMounted, ref, computed } from '@vue/composition-api'

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      passwordFieldTypeOld: "password",
      passwordFieldTypeNew: "password",
      passwordFieldTypeRetype: "password",

      required,
      confirmed,
      password,
    };
  },
  setup() {
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { changeCognitoPassword } = useLambda()
    const { getValidToken } = useCognito()

    const passwordValueOld = ref('')
    const newPasswordValue = ref('')
    const retypePassword = ref('')
    const reset = ref(null)
    const resetForm = ref(null)
    const isSubmitting = ref(false)

    const changePassword = async () => {
      try {
        isSubmitting.value = true
      
        const accessToken = await getValidToken()

        const result = await changeCognitoPassword({
          accessToken,
          oldPassword: passwordValueOld.value,
          newPassword: newPasswordValue.value,
        })
        if (result?.error) throw new Error(result.details)
        
        showSuccessMessage(i18n.t('message.password_updated'));
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.something_went_wrong'));
      } finally {
        isSubmitting.value = false
        passwordValueOld.value = ''
        newPasswordValue.value = ''
        retypePassword.value = ''
        resetForm.value?.reset()
      }
    }

    const requirements = computed(() => {
      const pwd = newPasswordValue.value || ''
      return [
        {
          key: 'length',
          met: passwordRequirements.length(pwd),
          text: i18n.t('message.password_req_length')
        },
        {
          key: 'uppercase',
          met: passwordRequirements.uppercase(pwd),
          text: i18n.t('message.password_req_uppercase')
        },
        {
          key: 'number',
          met: passwordRequirements.number(pwd),
          text: i18n.t('message.password_req_number')
        },
        {
          key: 'symbol',
          met: passwordRequirements.symbol(pwd),
          text: i18n.t('message.password_req_symbol')
        }
      ]
    })

    const allRequirementsMet = computed(() => {
      return requirements.value.every(req => req.met)
    })

    return {
      changePassword,
      passwordValueOld,
      newPasswordValue,
      retypePassword,
      reset,
      resetForm,
      isSubmitting,
      requirements,
      allRequirementsMet,
    }
  },
  computed: {
    passwordToggleIconOld() {
      return this.passwordFieldTypeOld === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    passwordToggleIconNew() {
      return this.passwordFieldTypeNew === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    passwordToggleIconRetype() {
      return this.passwordFieldTypeRetype === "password" ? "EyeIcon" : "EyeOffIcon";
    },
  },
  methods: {
    togglePasswordOld() {
      this.passwordFieldTypeOld =
        this.passwordFieldTypeOld === "password" ? "text" : "password";
    },
    togglePasswordNew() {
      this.passwordFieldTypeNew =
        this.passwordFieldTypeNew === "password" ? "text" : "password";
    },
    togglePasswordRetype() {
      this.passwordFieldTypeRetype =
        this.passwordFieldTypeRetype === "password" ? "text" : "password";
    },
  },
};
</script>

<style scoped>
.password-requirements {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.357rem;
}

.requirement-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.requirement-item:last-child {
  margin-bottom: 0;
}

.requirement-unmet {
  color: #ea5455;
}

.requirement-unmet svg {
  color: #ea5455;
}

.requirement-met {
  color: #28c76f;
}

.requirement-met svg {
  color: #28c76f;
}
</style>
